import { Bell, ChevronRight, Search, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { Input } from "@/modern/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modern/components/ui/select";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useLeadsContext, type LeadsSourceFilter, type LeadsSortMode } from "../context/LeadsContext";

const SOURCE_CHIPS: { value: LeadsSourceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "contact", label: "Contact" },
  { value: "ai_intake", label: "AI intake" },
];

const SORT_OPTIONS: { value: LeadsSortMode; label: string }[] = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc", label: "Oldest first" },
  { value: "name_asc", label: "Name A→Z" },
];

const PILL_BASE =
  "h-8 rounded-full border px-3.5 text-xs font-medium shadow-none transition-[color,box-shadow,background-color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background";

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        PILL_BASE,
        active
          ? "pointer-events-none border-transparent bg-primary/15 font-semibold text-foreground ring-1 ring-primary/20"
          : "border-border/35 bg-card text-muted-foreground hover:border-border/55 hover:bg-accent/35 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function LeadsToolbar() {
  const { viewManager } = useSwivuBryntumContext();
  const {
    query,
    setQuery,
    sortMode,
    setSortMode,
    sourceFilter,
    setSourceFilter,
  } = useLeadsContext();

  return (
    <div className="flex w-full shrink-0 flex-wrap items-center gap-4 border-b border-border bg-accent px-5 min-h-[3rem]">
      <div className="relative w-52 shrink-0">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search leads…"
          variant="background"
          inputSize="sm"
          className={cn(
            "h-8 w-full rounded-full border border-border bg-card py-0 pl-8 text-xs leading-none text-card-foreground md:text-xs",
            "placeholder:text-muted-foreground",
            "!ring-0 !ring-offset-0 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20",
            query ? "pr-8" : "pr-3.5",
          )}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <Select value={sortMode} onValueChange={(v) => setSortMode(v as LeadsSortMode)}>
        <SelectTrigger
          className={cn(
            PILL_BASE,
            "w-[9.5rem] justify-between border-border bg-card text-muted-foreground",
            "!h-8 !min-h-8 !py-0 hover:border-border/55 hover:bg-accent/35 hover:text-foreground",
            "[&>svg]:size-3.5 [&>svg]:opacity-50",
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-xs">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex flex-wrap items-center gap-1">
        {SOURCE_CHIPS.map((chip) => (
          <FilterPill
            key={chip.value}
            active={sourceFilter === chip.value}
            onClick={() => setSourceFilter(chip.value)}
          >
            {chip.label}
          </FilterPill>
        ))}
      </div>

      {viewManager && (
        <button
          type="button"
          onClick={() =>
            viewManager.openAnonymousViewByType({
              viewType: VIEW_TYPES.settings,
              viewName: "Settings",
              otherParams: { setting: SETTING_KEYS.NOTIFICATIONS },
            })
          }
          className={cn(
            PILL_BASE,
            "ml-auto inline-flex items-center gap-1.5 border-border bg-card text-foreground hover:border-primary/30 hover:bg-accent/35",
          )}
        >
          <Bell className="size-3 text-primary" aria-hidden />
          Configure notifications for new leads
          <ChevronRight className="size-3 text-muted-foreground" aria-hidden />
        </button>
      )}
    </div>
  );
}
